import {
  Fragment,
  useEffect,
  useId,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type MutableRefObject,
} from 'react'
import { CheckIcon, CloseIcon, CopyIcon, EditIcon, TrashIcon } from '../icons/icons'
import { DropdownMenu } from './DropdownMenu'
import { IconButton } from './IconButton'
import { useFieldDensity } from './fieldDensity'
import {
  beginPillDrag,
  endPillDrag,
  joinEmails,
  markPillDragDropped,
  pillDragFromHere,
  pillDragOver,
  type PillDragItems,
} from './pillDrag'

interface EmailListInputProps {
  values: string[]
  onChange: (values: string[]) => void
  /**
   * The addresses being typed, before Enter, Tab, a comma or leaving the field
   * adds them; empty unless every one is valid. A form counts them so its
   * button is ready as soon as an address is typed.
   */
  onPendingChange?: (emails: string[]) => void
  placeholder?: string
  disabled?: boolean
  ariaLabel: string
  ariaInvalid?: boolean
  ariaRequired?: boolean
  onBlur?: () => void
  className?: string
  /** Chips of another kind, before the typed addresses in the same box. */
  leading?: ReactNode
  /** A control at the box's end, level with its first line (an add button). */
  trailing?: ReactNode
  /** The text input, for a host that focuses it (its add button). */
  inputRef?: MutableRefObject<HTMLInputElement | null>
  /**
   * Lets the address pills be dragged to the other boxes in this scope (an
   * email's To, CC and BCC), and theirs dropped here. A dropped pill moves.
   */
  dragScope?: string
  /** This box in a drag, for a host that drags chips of its own from it. */
  dragSourceId?: string
  /** What else another box's drag carried (its host's own chips) to add here. */
  onDropItems?: (items: Omit<PillDragItems, 'emails'>) => void
}

/** Where pills dragged within their box will land. */
const INSERT_BAR = (
  <span aria-hidden="true" className="h-5 w-0.5 shrink-0 rounded bg-[color:var(--accent-info)]" />
)

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function splitEmails(value: string) {
  return value
    .split(/[;,\s]+/)
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean)
}

/** An address being corrected in place of its chip. */
interface ChipEdit {
  email: string
  draft: string
  invalid: boolean
}

/**
 * Typed addresses as chips, with the text box after them. A chip can be
 * picked like text: a click picks it (Shift a run, Ctrl or ⌘ one more), the
 * arrow keys move along them from the box, and Ctrl+A picks them all while
 * the box is empty. Picked chips copy and cut as `a@x.com; b@y.com`, and
 * Backspace or Delete removes them; from an empty box the first press picks
 * the last chip and the second removes it. A right click offers the same.
 */
export default function EmailListInput({
  values,
  onChange,
  onPendingChange,
  placeholder = 'Enter email addresses...',
  disabled = false,
  ariaLabel,
  ariaInvalid,
  ariaRequired,
  onBlur,
  className = '',
  leading,
  trailing,
  inputRef,
  dragScope,
  dragSourceId,
  onDropItems,
}: EmailListInputProps) {
  const compact = useFieldDensity() === 'compact'
  const [draft, setDraft] = useState('')
  const [hasInvalidDraft, setHasInvalidDraft] = useState(false)
  const [edit, setEdit] = useState<ChipEdit | null>(null)
  const [picked, setPicked] = useState<string[]>([])
  const [menuEmail, setMenuEmail] = useState<string | null>(null)
  const [dropping, setDropping] = useState(false)
  // Where pills dragged within this box would go, by index in `values`.
  const [insertAt, setInsertAt] = useState<number | null>(null)
  // A run of picked chips goes from the anchor to the end the keys move.
  const anchorRef = useRef<string | null>(null)
  const focusRef = useRef<string | null>(null)
  const draggedRef = useRef<string[]>([])
  const ownDragId = useId()
  const dragId = dragSourceId ?? ownDragId
  const ownInputRef = useRef<HTMLInputElement | null>(null)
  const textInputRef = inputRef ?? ownInputRef
  const editInputRef = useRef<HTMLInputElement | null>(null)
  // Set once Enter, Escape or a button closes the edit, so its input's blur as
  // it goes doesn't commit it again.
  const editClosedRef = useRef(false)
  const chipsRef = useRef<HTMLDivElement | null>(null)
  const pillRefs = useRef(new Map<string, HTMLElement>())
  const chipHeightClass = compact ? 'h-5' : 'h-[22px]'
  // In the box's order, whatever order they were picked in.
  const selection = values.filter((email) => picked.includes(email))
  const canDrag = Boolean(dragScope) && !disabled

  // A field that goes away leaves nothing pending.
  const onPendingChangeRef = useRef(onPendingChange)
  onPendingChangeRef.current = onPendingChange
  useEffect(() => () => onPendingChangeRef.current?.([]), [])

  // A chip opened for editing starts with its address selected.
  const editingEmail = edit?.email
  useEffect(() => {
    if (editingEmail === undefined) return
    editClosedRef.current = false
    editInputRef.current?.focus()
    editInputRef.current?.select()
  }, [editingEmail])

  const focusInput = () => textInputRef.current?.focus()

  const pick = (emails: string[], anchor = emails[emails.length - 1] ?? null) => {
    setPicked(emails)
    anchorRef.current = anchor
    focusRef.current = emails[emails.length - 1] ?? null
  }

  const removeEmails = (emails: readonly string[]) => {
    onChange(values.filter((email) => !emails.includes(email)))
    pick([])
  }

  const updateDraft = (next: string) => {
    setDraft(next)
    if (!onPendingChange) return
    const candidates = splitEmails(next)
    onPendingChange(candidates.every((email) => EMAIL_PATTERN.test(email)) ? candidates : [])
  }

  /** Adds what's typed as chips; false when something typed isn't an address. */
  const commitDraft = () => {
    const candidates = splitEmails(draft)
    if (candidates.length === 0) return true
    if (candidates.some((email) => !EMAIL_PATTERN.test(email))) {
      setHasInvalidDraft(true)
      return false
    }
    onChange([...new Set([...values, ...candidates])])
    updateDraft('')
    setHasInvalidDraft(false)
    return true
  }

  /** The corrected address takes its chip's place; emptied, the chip goes. */
  const commitEdit = () => {
    if (!edit || editClosedRef.current) return
    const candidates = splitEmails(edit.draft)
    if (candidates.some((email) => !EMAIL_PATTERN.test(email))) {
      setEdit({ ...edit, invalid: true })
      return
    }
    const index = values.indexOf(edit.email)
    onChange([...new Set([...values.slice(0, index), ...candidates, ...values.slice(index + 1)])])
    editClosedRef.current = true
    setEdit(null)
  }

  const cancelEdit = () => {
    editClosedRef.current = true
    setEdit(null)
    focusInput()
  }

  const startEdit = (email: string) => {
    pick([])
    setEdit({ email, draft: email, invalid: false })
  }

  /** The arrow keys move along the chips; with Shift, the run grows or shrinks. */
  const moveSelection = (step: -1 | 1, extend: boolean) => {
    const from = focusRef.current ? values.indexOf(focusRef.current) : values.length
    const next = from + step
    if (next >= values.length) {
      // Past the last chip, back to typing.
      pick([])
      return
    }
    const email = values[Math.max(0, next)]
    const anchor = extend && anchorRef.current ? anchorRef.current : email
    const [start, end] = [values.indexOf(anchor), values.indexOf(email)].sort((a, b) => a - b)
    setPicked(values.slice(start, end + 1))
    anchorRef.current = anchor
    focusRef.current = email
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const modified = event.ctrlKey || event.metaKey
    if (selection.length > 0) {
      if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault()
        removeEmails(selection)
        return
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        pick([])
        return
      }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault()
        moveSelection(event.key === 'ArrowLeft' ? -1 : 1, event.shiftKey)
        return
      }
    }
    if (draft === '' && values.length > 0) {
      if (event.key === 'Backspace' || event.key === 'Delete' || event.key === 'ArrowLeft') {
        // The first press picks the last chip; a second Backspace removes it.
        event.preventDefault()
        pick([values[values.length - 1]])
        return
      }
      if (modified && event.key.toLowerCase() === 'a') {
        event.preventDefault()
        pick([...values], values[0])
        return
      }
    }
    if (event.key === 'Tab' && !event.shiftKey && draft.trim() !== '') {
      // A valid address becomes a chip and the caret stays after it; an
      // invalid one is marked and Tab goes on (to the add button).
      if (commitDraft()) event.preventDefault()
      return
    }
    if (event.key !== 'Enter' && event.key !== ',' && event.key !== ';') return
    event.preventDefault()
    commitDraft()
  }

  const handleEditKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      cancelEdit()
    } else if (event.key === 'Enter') {
      event.preventDefault()
      commitEdit()
    }
  }

  const handlePillClick = (event: MouseEvent<HTMLElement>, email: string) => {
    if (disabled) return
    if (event.shiftKey && anchorRef.current && values.includes(anchorRef.current)) {
      const [start, end] = [values.indexOf(anchorRef.current), values.indexOf(email)].sort(
        (a, b) => a - b
      )
      setPicked(values.slice(start, end + 1))
      focusRef.current = email
    } else if (event.ctrlKey || event.metaKey) {
      setPicked((current) =>
        current.includes(email) ? current.filter((item) => item !== email) : [...current, email]
      )
      anchorRef.current = email
      focusRef.current = email
    } else {
      pick([email])
    }
    // Keys go on working from the box: Delete, the arrows, Ctrl+C.
    focusInput()
  }

  const handlePillContextMenu = (event: MouseEvent<HTMLElement>, email: string) => {
    if (disabled) return
    event.preventDefault()
    if (!selection.includes(email)) pick([email])
    setMenuEmail(email)
  }

  // The chips the menu or a drag acts on: the picked run it's in, or just it.
  const chipsFor = (email: string) => (selection.includes(email) ? selection : [email])

  const copyText = (emails: readonly string[]) => {
    void navigator.clipboard?.writeText(joinEmails(emails))
  }

  const handleInputCopy = (event: ClipboardEvent<HTMLInputElement>, cut: boolean) => {
    if (selection.length === 0) return
    event.preventDefault()
    event.clipboardData.setData('text/plain', joinEmails(selection))
    if (cut) removeEmails(selection)
  }

  // Chips highlighted with the mouse copy as addresses too, not as their text.
  const handleBoxCopy = (event: ClipboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented) return
    const highlighted = window.getSelection()
    if (!highlighted || highlighted.isCollapsed) return
    const emails = values.filter((email) => {
      const element = pillRefs.current.get(email)
      return element ? highlighted.containsNode(element, true) : false
    })
    if (emails.length === 0) return
    event.preventDefault()
    event.clipboardData.setData('text/plain', joinEmails(emails))
  }

  const handlePillDragStart = (event: DragEvent<HTMLElement>, email: string) => {
    if (!dragScope) return
    const emails = chipsFor(email)
    if (!selection.includes(email)) pick([email])
    draggedRef.current = emails
    beginPillDrag(dragScope, dragId, { emails })
    event.dataTransfer.effectAllowed = 'copyMove'
    event.dataTransfer.setData('text/plain', joinEmails(emails))
  }

  const handlePillDragEnd = () => {
    setInsertAt(null)
    if (endPillDrag(dragId)) removeEmails(draggedRef.current)
    draggedRef.current = []
  }

  const dragHere = () => (dragScope ? pillDragOver(dragScope, dragId) : null)

  // Its own pills dragged within it: where they'd go, or null when there's
  // nowhere else for them (the box's only pills).
  const reorderTarget = (event: DragEvent<HTMLDivElement>) => {
    const own = dragScope ? pillDragFromHere(dragScope, dragId) : null
    const dragged = own?.emails ?? []
    if (dragged.length === 0 || dragged.length >= values.length) return null
    const over = (event.target as Element | null)?.closest?.<HTMLElement>('[data-email-chip]')
    const email = over?.dataset.emailChip
    if (!over || !email || !values.includes(email)) return values.length
    const rect = over.getBoundingClientRect()
    const after = event.clientX > rect.left + rect.width / 2
    return values.indexOf(email) + (after ? 1 : 0)
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (dragScope && pillDragFromHere(dragScope, dragId)) {
      // Never dropped as text into its own box: reordered, or not at all.
      event.preventDefault()
      const at = reorderTarget(event)
      event.dataTransfer.dropEffect = at === null ? 'none' : 'move'
      setInsertAt(at)
      return
    }
    if (!dragHere()) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    setDropping(true)
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (dragScope && pillDragFromHere(dragScope, dragId)) {
      event.preventDefault()
      const at = reorderTarget(event)
      setInsertAt(null)
      const dragged = pillDragFromHere(dragScope, dragId)?.emails ?? []
      if (at === null) return
      const before = values.slice(0, at).filter((email) => !dragged.includes(email))
      const after = values.slice(at).filter((email) => !dragged.includes(email))
      const reordered = [...before, ...values.filter((email) => dragged.includes(email)), ...after]
      if (reordered.some((email, index) => email !== values[index])) onChange(reordered)
      return
    }
    const items = dragHere()
    setDropping(false)
    if (!items) return
    // Not as text into the box under it.
    event.preventDefault()
    markPillDragDropped()
    const { emails, ...rest } = items
    if (emails?.length) onChange([...new Set([...values, ...emails])])
    if (Object.keys(rest).length > 0) onDropItems?.(rest)
  }

  // A press on the box's empty space types into it, as a text field would.
  const handleBoxMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (disabled) return
    if (event.target !== event.currentTarget && event.target !== chipsRef.current) return
    event.preventDefault()
    pick([])
    focusInput()
  }

  const pillMenu = (email: string) => {
    const emails = chipsFor(email)
    const count = emails.length > 1 ? ` ${emails.length} addresses` : ''
    return [
      {
        items: [
          {
            type: 'action' as const,
            id: 'copy',
            label: `Copy${count}`,
            icon: <CopyIcon size={14} aria-hidden="true" />,
            onClick: () => copyText(emails),
          },
          {
            type: 'action' as const,
            id: 'edit',
            label: 'Edit',
            icon: <EditIcon size={14} aria-hidden="true" />,
            omitted: emails.length > 1,
            onClick: () => startEdit(email),
          },
          {
            type: 'action' as const,
            id: 'remove',
            label: `Remove${count}`,
            tone: 'danger' as const,
            icon: <TrashIcon size={14} aria-hidden="true" />,
            onClick: () => {
              removeEmails(emails)
              focusInput()
            },
          },
        ],
      },
    ]
  }

  return (
    <div
      onMouseDown={handleBoxMouseDown}
      onCopy={handleBoxCopy}
      onDragOver={handleDragOver}
      onDragLeave={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
        setDropping(false)
        setInsertAt(null)
      }}
      onDrop={handleDrop}
      className={`flex w-full items-start gap-1 rounded border focus-within:border-[color:var(--accent-info)] ${
        // A trailing button sits 6px from the top, right and bottom at any
        // density, as the email step's other add buttons do.
        trailing
          ? 'min-h-9 py-1.5 pl-2 pr-1.5'
          : compact
            ? 'min-h-[32px] px-2 py-1'
            : 'min-h-9 px-2 py-1.5'
      } ${
        ariaInvalid || hasInvalidDraft
          ? 'border-danger'
          : dropping
            ? 'border-[color:var(--accent-info)]'
            : 'border-default'
      } ${
        disabled
          ? 'bg-muted opacity-70'
          : dropping
            ? 'bg-[color:var(--surface-info-subtle)]'
            : 'surface-default'
      } ${className}`}
    >
      <div ref={chipsRef} className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
        {leading}
        {values.map((email, index) => {
          const bar = insertAt === index ? INSERT_BAR : null
          if (edit?.email === email) {
            return (
              <Fragment key={email}>
                {bar}
                <span
                  className={`inline-flex ${chipHeightClass} max-w-full min-w-0 items-center rounded border pr-0.5 ${
                    edit.invalid ? 'border-danger' : 'border-[color:var(--accent-info)]'
                  }`}
                >
                  <input
                    ref={editInputRef}
                    type="email"
                    value={edit.draft}
                    size={Math.max(edit.draft.length, 12)}
                    aria-label={`Edit ${email}`}
                    aria-invalid={edit.invalid || undefined}
                    onChange={(event) =>
                      setEdit({ ...edit, draft: event.target.value, invalid: false })
                    }
                    onKeyDown={handleEditKeyDown}
                    onBlur={commitEdit}
                    className="h-full min-w-0 max-w-full bg-transparent px-1.5 text-[11px] text-default outline-none"
                  />
                  {/* As a page title's rename: the red X keeps the address, the check saves it. */}
                  <IconButton
                    label="Cancel editing"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={cancelEdit}
                    wrapperClassName="inline-flex shrink-0"
                    className="inline-flex h-4 w-4 items-center justify-center rounded text-danger transition-colors hover:bg-[color:var(--surface-danger-subtle)]"
                  >
                    <CloseIcon size={10} aria-hidden="true" />
                  </IconButton>
                  <IconButton
                    label={`Save ${edit.draft.trim() || email}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={commitEdit}
                    wrapperClassName="inline-flex shrink-0"
                    className="inline-flex h-4 w-4 items-center justify-center rounded text-success transition-colors hover:bg-[color:color-mix(in_srgb,var(--text-success)_12%,transparent)]"
                  >
                    <CheckIcon size={11} aria-hidden="true" />
                  </IconButton>
                </span>
              </Fragment>
            )
          }
          const isPicked = selection.includes(email)
          return (
            <Fragment key={email}>
              {bar}
              <DropdownMenu
                open={menuEmail === email}
                onOpenChange={(open) => setMenuEmail(open ? email : null)}
                triggerMode="manual"
                align="left"
                wrapperClassName="inline-flex max-w-full"
                sections={pillMenu(email)}
                trigger={
                  <span
                    ref={(element) => {
                      if (element) pillRefs.current.set(email, element)
                      else pillRefs.current.delete(email)
                    }}
                    data-email-chip={email}
                    data-picked={isPicked || undefined}
                    draggable={canDrag}
                    onClick={(event) => handlePillClick(event, email)}
                    onContextMenu={(event) => handlePillContextMenu(event, email)}
                    onDragStart={(event) => handlePillDragStart(event, email)}
                    onDragEnd={handlePillDragEnd}
                    className={`inline-flex ${chipHeightClass} max-w-full cursor-pointer items-center gap-1 rounded border px-1.5 text-[11px] transition-colors ${
                      isPicked
                        ? // Picked, like selected text: white on the accent in both themes.
                          'border-[color:var(--accent-info)] bg-[color:var(--accent-info)] text-white'
                        : 'border-[color:var(--border-info)] bg-[color:var(--surface-info-subtle)] text-[color:var(--accent-info)] hover:bg-[color:var(--surface-info-subtle-hover)]'
                    }`}
                  >
                    <span className="truncate">{email}</span>
                    <IconButton
                      label={`Edit ${email}`}
                      disabled={disabled}
                      onClick={(event) => {
                        event.stopPropagation()
                        startEdit(email)
                      }}
                      wrapperClassName="inline-flex shrink-0"
                      className={`inline-flex transition-colors disabled:cursor-not-allowed ${
                        isPicked
                          ? // On the accent, as the address is.
                            'text-white/80 hover:text-white'
                          : 'text-muted hover:text-default'
                      }`}
                    >
                      <EditIcon size={9} aria-hidden="true" />
                    </IconButton>
                    <IconButton
                      label={`Remove ${email}`}
                      disabled={disabled}
                      onClick={(event) => {
                        event.stopPropagation()
                        removeEmails([email])
                      }}
                      wrapperClassName="inline-flex shrink-0"
                      className={`inline-flex transition-colors disabled:cursor-not-allowed ${
                        isPicked
                          ? // On the accent, as the address is.
                            'text-white/80 hover:text-white'
                          : 'text-muted hover:text-default'
                      }`}
                    >
                      <CloseIcon size={9} aria-hidden="true" />
                    </IconButton>
                  </span>
                }
              />
            </Fragment>
          )
        })}
        {insertAt === values.length ? INSERT_BAR : null}
        <input
          ref={textInputRef}
          type="email"
          multiple
          value={draft}
          disabled={disabled}
          placeholder={values.length === 0 && !leading ? placeholder : undefined}
          aria-label={ariaLabel}
          aria-invalid={ariaInvalid || hasInvalidDraft || undefined}
          aria-required={ariaRequired}
          onChange={(event) => {
            updateDraft(event.target.value)
            setHasInvalidDraft(false)
            pick([])
          }}
          onKeyDown={handleKeyDown}
          onCopy={(event) => handleInputCopy(event, false)}
          onCut={(event) => handleInputCopy(event, true)}
          onPaste={(event) => {
            const pasted = event.clipboardData.getData('text')
            if (!/[;,\s]/.test(pasted)) return
            event.preventDefault()
            const candidates = splitEmails(pasted)
            if (candidates.some((email) => !EMAIL_PATTERN.test(email))) {
              updateDraft(pasted)
              setHasInvalidDraft(true)
              return
            }
            onChange([...new Set([...values, ...candidates])])
          }}
          onBlur={() => {
            commitDraft()
            onBlur?.()
          }}
          className={`h-6 min-w-[160px] flex-1 border-0 bg-transparent px-1 leading-5 font-[450] text-default outline-none placeholder:font-light placeholder:italic placeholder:text-muted disabled:cursor-not-allowed ${
            compact ? 'text-sm' : 'text-base'
          }`}
        />
      </div>
      {trailing && <div className="flex h-6 shrink-0 items-center">{trailing}</div>}
    </div>
  )
}
